
import { parseISO, format, startOfDay, isValid, eachDayOfInterval } from "date-fns";
import { Trip } from "@/types/itinerary";

/**
 * Parses a date string safely with multiple fallback strategies
 */
export const safeParseDateString = (dateString: string): Date => {
  if (!dateString) {
    console.error('Empty date string provided to safeParseDateString');
    return new Date(); // Fallback to current date
  }
  
  // Log the raw date for debugging
  console.log('Raw date in safeParseDateString:', dateString, 'type:', typeof dateString);
  
  // Try parsing as ISO first
  try {
    const parsed = parseISO(String(dateString));
    if (isValid(parsed)) {
      console.log('Successfully parsed date as ISO:', parsed);
      return parsed;
    }
  } catch (e) {
    console.warn('ISO parsing failed:', e);
  }
  
  // Try direct Date constructor
  try {
    // Add time component if missing (like "2025-06-05" to "2025-06-05T00:00:00")
    let dateStr = String(dateString);
    if (dateStr.length <= 10 && dateStr.includes('-')) {
      dateStr = `${dateStr}T00:00:00`;
    }
    
    const parsed = new Date(dateStr);
    if (isValid(parsed)) {
      console.log('Successfully parsed date with constructor:', parsed);
      return parsed;
    }
  } catch (e) {
    console.warn('Direct parsing failed:', e);
  }
  
  // If it's a numeric timestamp
  if (!isNaN(Number(dateString))) {
    try {
      const parsed = new Date(Number(dateString));
      if (isValid(parsed)) {
        console.log('Successfully parsed numeric timestamp:', parsed);
        return parsed;
      }
    } catch (e) {
      console.warn('Numeric parsing failed:', e);
    }
  }
  
  // Last resort: try parsing parts manually for YYYY-MM-DD format
  if (String(dateString).includes('-') && String(dateString).length >= 10) {
    try {
      const [year, month, day] = String(dateString).substring(0, 10).split('-').map(Number);
      // Note: month is 0-indexed in JS Date
      const parsed = new Date(year, month - 1, day);
      if (isValid(parsed)) {
        console.log('Successfully parsed date manually:', parsed);
        return parsed;
      }
    } catch (e) {
      console.warn('Manual date parsing failed:', e);
    }
  }
  
  console.error(`Failed to parse date: ${dateString}, using current date as fallback`);
  return new Date(); // Fallback to current date
};

/**
 * Hook to generate an array of days covered by a trip
 */
export const useTripDays = (trip: Trip | null) => {
  const getTripDays = (): string[] => {
    if (!trip || !trip.start_date || !trip.end_date) {
      console.log('Trip or trip dates missing in getTripDays, returning empty array');
      return [];
    }
    
    try {
      // Ensure we have proper date strings
      const startDateStr = String(trip.start_date);
      const endDateStr = String(trip.end_date);
      
      // Log for debugging
      console.log('Raw start_date:', startDateStr, 'type:', typeof startDateStr);
      console.log('Raw end_date:', endDateStr, 'type:', typeof endDateStr);
      
      // Parse dates with our robust parser
      let startDate, endDate;
      
      try {
        startDate = safeParseDateString(startDateStr);
        endDate = safeParseDateString(endDateStr);
        
        console.log('Successfully parsed start date:', startDate);
        console.log('Successfully parsed end date:', endDate);
      } catch (e) {
        console.error('Critical error parsing trip dates:', e);
        return [];
      }
      
      // Start dates at the beginning of the day
      const startOfDayDate = startOfDay(startDate);
      const endOfDayDate = startOfDay(endDate);
      
      // Ensure end date is not before start date
      if (endOfDayDate < startOfDayDate) {
        console.error('End date is before start date, swapping dates');
        return eachDayOfInterval({ 
          start: endOfDayDate, 
          end: startOfDayDate 
        }).map(day => format(day, 'yyyy-MM-dd'));
      }
      
      console.log('Generating trip days from:', startOfDayDate, 'to:', endOfDayDate);
      
      const daysArray = eachDayOfInterval({ 
        start: startOfDayDate, 
        end: endOfDayDate 
      });
      
      const formattedDays = daysArray.map(day => format(day, 'yyyy-MM-dd'));
      console.log('Generated trip days:', formattedDays);
      
      return formattedDays;
    } catch (error) {
      console.error("Error generating trip days:", error);
      console.error("Start date:", trip.start_date);
      console.error("End date:", trip.end_date);
      
      // Emergency fallback - generate days between today and a week from now
      console.log("Using emergency fallback for trip days");
      const today = new Date();
      const weekLater = new Date();
      weekLater.setDate(today.getDate() + 7);
      
      return eachDayOfInterval({ 
        start: today, 
        end: weekLater 
      }).map(day => format(day, 'yyyy-MM-dd'));
    }
  };
  
  return getTripDays();
};
